import React, {useState, useEffect} from 'react';
import {useMediaQuery} from 'react-responsive';
import useLoanCalculator from '../hooks/useLoanCalculator';
import LoanForm from './LoanForm';
import SecciPopup from './SecciPopup';
import './LoanCalculator.css';
import WarningFooter from "./WarningFooter";

const LoanCalculator = ({appConfig}) => {
    const {applyUrl, footerText, loginLabel} = appConfig;
    const [showPopup, setShowPopup] = useState(false);
    const [showError, setShowError] = useState(false);
    const [fadeIn, setFadeIn] = useState(false);

    const isMobile = useMediaQuery({maxWidth: 767});

    const {
        loading,
        error,
        productData,
        amountSelected,
        durationSelected,
        currency,
        calculateInstallment,
        handleAmountChange,
        handleDurationChange,
        fetchSecciDocument,
        getValidDurations
    } = useLoanCalculator(appConfig);

    useEffect(() => {
        if (!loading && !error) {
            const timer = setTimeout(() => {
                setFadeIn(true);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [loading, error]);

    useEffect(() => {
        if (error) {
            setShowError(true);
        }
    }, [error]);

    const handleSubmit = (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.append('amount', amountSelected);
        formData.append('product', appConfig.productId);
        formData.append('offer', appConfig.productId);
        formData.append('duration', durationSelected * 30);

        const params = new URLSearchParams(window.location.search);
        params.forEach((value, key) => {
            formData.append(key, value);
        });

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = applyUrl;

        for (const [key, value] of formData.entries()) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = value;
            form.appendChild(input);
        }

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    };

    const handleSecciRequest = () => {
        setShowPopup(true);
    };

    const placeholderProductData = {
        amountRange: [5000, 10000],
        durationRange: ["1M", "12M"],
        defaultAmount: 5000,
        defaultDuration: "6M"
    };

    const installmentDetails = !loading && !error ? calculateInstallment() : {principal: 0, installmentAmount: 0};

    const isInactiveState = loading || error;

    return (
        <div className={`loan-calculator ${fadeIn ? 'fade-in' : ''}`}>
            {showError && (
                <div className="error-message">
                    {appConfig.apiErrorMessage || 'Something went wrong, try again later'}
                </div>
            )}

            <div className="calculator-container">
                <LoanForm
                    productData={isInactiveState ? placeholderProductData : productData}
                    amountSelected={amountSelected || 0}
                    durationSelected={durationSelected || 0}
                    currency={currency || '€'}
                    installmentDetails={installmentDetails}
                    handleAmountChange={handleAmountChange}
                    handleDurationChange={handleDurationChange}
                    onSubmit={handleSubmit}
                    onSecciRequest={handleSecciRequest}
                    getValidDurations={getValidDurations}
                    isMobile={isMobile}
                    labels={appConfig}
                    isLoading={isInactiveState}
                />
            </div>

            {loginLabel && (
                <div className="login-container">
                    <div dangerouslySetInnerHTML={{__html: loginLabel}}/>
                </div>
            )}
            {footerText && <div className="calculator-footer">
                <div dangerouslySetInnerHTML={{__html: footerText}}/>
            </div>}


            <SecciPopup
                isOpen={showPopup}
                onClose={() => setShowPopup(false)}
                fetchDocument={fetchSecciDocument}
            />
            {appConfig.warningContent && (
                <WarningFooter
                    title={appConfig.warningTitle || undefined}
                    content={appConfig.warningContent}
                />
            )}
        </div>
    );
};

export default LoanCalculator;
